import { useState } from "react";
import { createCredential, updateCredential } from "../../services/credentialService.js";

// ─────────────────────────────────────────────
//  TYPES — alignés sur CredentialType.java
// ─────────────────────────────────────────────

const TYPES = [
  { id: "WEBSITE",     label: "Site Web",        bg: "#3B5BDB" },
  { id: "APPLICATION", label: "Application",     bg: "#6741D9" },
  { id: "DATABASE",    label: "Base de données", bg: "#C2255C" },
  { id: "EMAIL",       label: "E-mail",          bg: "#2F9E44" },
  { id: "SERVER",      label: "Serveur",         bg: "#3D6B6B" },
  { id: "SSH",         label: "SSH",             bg: "#1A1A1A" },
  { id: "VPN",         label: "VPN",             bg: "#0077B6" },
  { id: "WIFI",        label: "WiFi",            bg: "#9B2AE8" },
  { id: "ROUTER",      label: "Routeur",         bg: "#E85D04" },
  { id: "OTHER",       label: "Autre",           bg: "#F76707" },
];

// ─────────────────────────────────────────────
//  ILLUSTRATIONS SVG PAR TYPE (bannière modale)
// ─────────────────────────────────────────────

const SVG = {
  WEBSITE: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g1" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#4DABF7"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g1)"/>
      <circle cx="80" cy="45" r="26" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="2.5"/>
      <ellipse cx="80" cy="45" rx="11" ry="26" fill="none" stroke="rgba(255,255,255,.7)" strokeWidth="1.8"/>
      <line x1="54" y1="45" x2="106" y2="45" stroke="rgba(255,255,255,.7)" strokeWidth="1.8"/>
    </svg>
  ),
  APPLICATION: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g2" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#6741D9"/><stop offset="100%" stopColor="#9775FA"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g2)"/>
      <circle cx="80" cy="44" r="16" fill="rgba(255,255,255,.9)"/>
      <polyline points="80,58 80,38" stroke="#6741D9" strokeWidth="3" strokeLinecap="round"/>
      <polyline points="72,46 80,38 88,46" stroke="#6741D9" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  DATABASE: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g3" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#C2255C"/><stop offset="100%" stopColor="#F06595"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g3)"/>
      <ellipse cx="80" cy="28" rx="24" ry="8" fill="rgba(255,255,255,.88)"/>
      <rect x="56" y="28" width="48" height="14" fill="rgba(255,255,255,.75)"/>
      <ellipse cx="80" cy="42" rx="24" ry="8" fill="rgba(255,255,255,.88)"/>
      <rect x="56" y="42" width="48" height="14" fill="rgba(255,255,255,.65)"/>
      <ellipse cx="80" cy="56" rx="24" ry="8" fill="rgba(255,255,255,.88)"/>
    </svg>
  ),
  EMAIL: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g4" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#2F9E44"/><stop offset="100%" stopColor="#69DB7C"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g4)"/>
      <rect x="38" y="26" width="84" height="52" rx="4" fill="rgba(255,255,255,.88)"/>
      <polyline points="38,26 80,56 122,26" fill="none" stroke="rgba(47,158,68,.8)" strokeWidth="2.5"/>
    </svg>
  ),
  SERVER: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g5" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#3D6B6B"/><stop offset="100%" stopColor="#63B3B3"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g5)"/>
      <rect x="52" y="12" width="56" height="66" rx="5" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.6)" strokeWidth="1.5"/>
      {[18,30,42,54].map((y, i) => <g key={i}><rect x="55" y={y} width="50" height="9" rx="2" fill="rgba(255,255,255,.18)"/><circle cx="98" cy={y+4.5} r="2.5" fill={i<2?"#69DB7C":"#FFD43B"}/></g>)}
    </svg>
  ),
  SSH: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <rect width="160" height="90" fill="#1A1A1A"/>
      <rect x="22" y="16" width="116" height="62" rx="6" fill="#0D1117"/>
      <circle cx="33" cy="24" r="4" fill="#FF5F57"/><circle cx="46" cy="24" r="4" fill="#FFBD2E"/><circle cx="59" cy="24" r="4" fill="#28C840"/>
      <text x="30" y="46" fontFamily="monospace" fontSize="8" fill="#58A6FF">$</text>
      <text x="40" y="46" fontFamily="monospace" fontSize="8" fill="#7EE787">ssh admin@192.168.1.1</text>
    </svg>
  ),
  VPN: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g7" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#0077B6"/><stop offset="100%" stopColor="#00B4D8"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g7)"/>
      <circle cx="80" cy="45" r="28" fill="none" stroke="rgba(255,255,255,.3)" strokeWidth="1.5"/>
      <circle cx="80" cy="45" r="18" fill="none" stroke="rgba(255,255,255,.6)" strokeWidth="1.5"/>
      <circle cx="80" cy="45" r="8"  fill="rgba(255,255,255,.85)"/>
    </svg>
  ),
  WIFI: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g8" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#9B2AE8"/><stop offset="100%" stopColor="#DA77F2"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g8)"/>
      <circle cx="80" cy="74" r="6" fill="white"/>
      <path d="M62 60 Q80 46 98 60" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="4.5" strokeLinecap="round"/>
      <path d="M48 48 Q80 28 112 48" fill="none" stroke="rgba(255,255,255,.7)" strokeWidth="4.5" strokeLinecap="round"/>
      <path d="M34 36 Q80 10 126 36" fill="none" stroke="rgba(255,255,255,.45)" strokeWidth="4.5" strokeLinecap="round"/>
    </svg>
  ),
  ROUTER: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g9" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#E85D04"/><stop offset="100%" stopColor="#F48C06"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g9)"/>
      <rect x="40" y="44" width="80" height="20" rx="5" fill="rgba(255,255,255,.85)"/>
      {[56,68,80,92,104].map(x => <circle key={x} cx={x} cy="54" r="3" fill="#E85D04"/>)}
      <line x1="60" y1="44" x2="50" y2="28" stroke="rgba(255,255,255,.8)" strokeWidth="2"/>
      <line x1="100" y1="44" x2="110" y2="28" stroke="rgba(255,255,255,.8)" strokeWidth="2"/>
    </svg>
  ),
  OTHER: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <rect width="160" height="90" fill="#f1f3f5"/>
      <circle cx="80" cy="45" r="20" fill="#F76707"/>
      <rect x="77" y="33" width="6" height="24" rx="3" fill="white"/>
      <rect x="68" y="42" width="24" height="6" rx="3" fill="white"/>
    </svg>
  ),
};

// ─────────────────────────────────────────────
//  CHAMP MOT DE PASSE + INDICATEUR DE FORCE
// ─────────────────────────────────────────────

function PasswordField({ value, onChange, label = "Mot de passe", required = false }) {
  const [show, setShow] = useState(false);

  // Calcul de la force du mot de passe
  const getStrength = (pwd) => {
    if (!pwd) return null;
    let score = 0;
    if (pwd.length >= 8)           score++;
    if (pwd.length >= 12)          score++;
    if (/[A-Z]/.test(pwd))         score++;
    if (/[0-9]/.test(pwd))         score++;
    if (/[^A-Za-z0-9]/.test(pwd))  score++;
    const levels = [
      null,
      { label: "Très faible", color: "#fa5252" },
      { label: "Faible",      color: "#ff922b" },
      { label: "Moyen",       color: "#ffd43b" },
      { label: "Fort",        color: "#69db7c" },
      { label: "Très fort",   color: "#40c057" },
    ];
    return { score, ...levels[score] };
  };

  const strength = getStrength(value);

  return (
    <div className="mb-3">
      <label className="itm-label">
        {label} {required && <span style={{ color: "#fa5252" }}>*</span>}
      </label>
      <div className="input-group">
        <input
          type={show ? "text" : "password"}
          className="itm-input"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="••••••••"
        />
        <button type="button" className="itm-eye-btn" onClick={() => setShow(s => !s)} tabIndex={-1}>
          <i className={`bi ${show ? "bi-eye-slash" : "bi-eye"}`}/>
        </button>
      </div>
      {/* Barre de force */}
      {strength && (
        <div className="mt-1">
          <div style={{ display: "flex", gap: 4, marginBottom: 3 }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{ height: 3, flex: 1, borderRadius: 4, background: i <= strength.score ? strength.color : "rgba(255,255,255,.1)", transition: "background .3s" }}/>
            ))}
          </div>
          <small style={{ color: strength.color, fontWeight: 600, fontSize: ".74rem" }}>{strength.label}</small>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  FORMULAIRE — champs communs + champs par type
//  Aligné sur les champs de Credential.java :
//  name, username, encryptedPassword, url, host, port, notes, type
// ─────────────────────────────────────────────

function CredentialForm({ typeId, data, onChange, isEdit }) {
  const showHostPort  = ["SERVER", "SSH", "DATABASE", "ROUTER"].includes(typeId);
  const showUrl       = ["WEBSITE", "APPLICATION", "VPN", "OTHER"].includes(typeId);

  return (
    <>
      {/* Nom — obligatoire */}
      <div className="mb-3">
        <label className="itm-label">Nom <span style={{ color: "#fa5252" }}>*</span></label>
        <input
          className="itm-input"
          value={data.name || ""}
          onChange={e => onChange("name", e.target.value)}
          placeholder="Ex : Serveur prod, Gmail, GitHub…"
        />
      </div>

      {/* URL */}
      {showUrl && (
        <div className="mb-3">
          <label className="itm-label">URL</label>
          <input
            className="itm-input"
            value={data.url || ""}
            onChange={e => onChange("url", e.target.value)}
            placeholder="https://exemple.com"
          />
        </div>
      )}

      {/* Host + Port */}
      {showHostPort && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: 10 }} className="mb-3">
          <div>
            <label className="itm-label">Adresse IP / Hostname</label>
            <input
              className="itm-input"
              value={data.host || ""}
              onChange={e => onChange("host", e.target.value)}
              placeholder="192.168.1.10"
            />
          </div>
          <div>
            <label className="itm-label">Port</label>
            <input
              className="itm-input"
              value={data.port || ""}
              onChange={e => onChange("port", e.target.value)}
              placeholder={typeId === "SSH" ? "22" : typeId === "DATABASE" ? "5432" : "3389"}
            />
          </div>
        </div>
      )}

      {/* Nom d'utilisateur */}
      <div className="mb-3">
        <label className="itm-label">Nom d'utilisateur</label>
        <input
          className="itm-input"
          value={data.username || ""}
          onChange={e => onChange("username", e.target.value)}
          placeholder="john.doe, root, admin…"
        />
      </div>

      {/* Mot de passe */}
      <PasswordField
        value={data.encryptedPassword || ""}
        onChange={v => onChange("encryptedPassword", v)}
        required={!isEdit}
        label={isEdit ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe"}
      />

      {/* Notes */}
      <div className="mb-3">
        <label className="itm-label">Notes</label>
        <textarea
          className="itm-input"
          rows={3}
          value={data.notes || ""}
          onChange={e => onChange("notes", e.target.value)}
          placeholder="Informations supplémentaires…"
          style={{ resize: "vertical" }}
        />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
//  MODAL — SÉLECTION DU TYPE (étape 1)
// ─────────────────────────────────────────────

function TypePickerModal({ onSelect, onClose }) {
  return (
    <div className="itm-overlay" onClick={onClose}>
      <div className="itm-modal picker-modal" onClick={e => e.stopPropagation()}>
        <button className="itm-close-btn" onClick={onClose}><i className="bi bi-x-lg"/></button>
        <h5 className="itm-modal-title">Ajouter un mot de passe</h5>
        <p className="itm-modal-sub">Quel type de credential voulez-vous ajouter ?</p>
        <div className="type-grid">
          {TYPES.map(t => (
            <button key={t.id} className="type-tile" onClick={() => onSelect(t)}>
              <div className="type-banner">{SVG[t.id]}</div>
              <span className="type-label">{t.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  MODAL — FORMULAIRE (étape 2)
// ─────────────────────────────────────────────

// defaultScope : scope transmis par la page parente ("PERSONAL" depuis PasswordsPage)
function FormModal({ type, editData, defaultScope = "PERSONAL", onClose, onSave }) {
  const isEdit = !!editData;

  // Pré-remplissage en mode édition (sans le mot de passe chiffré)
  const [data, setData] = useState(
    isEdit
      ? { ...editData, encryptedPassword: "" }
      : { name: "", username: "", encryptedPassword: "", url: "", host: "", port: "", notes: "" }
  );
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handleChange = (key, val) => setData(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    // Validation
    if (!data.name?.trim()) {
      setError("Le nom est obligatoire.");
      return;
    }
    if (!isEdit && !data.encryptedPassword?.trim()) {
      setError("Le mot de passe est obligatoire.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Payload — aligné sur Credential.java
      const payload = {
        name:     data.name,
        username: data.username  || null,
        url:      data.url       || null,
        host:     data.host      || null,
        port:     data.port      ? parseInt(data.port) : null,
        notes:    data.notes     || null,
        type:     type.id,
        // Scope : "PERSONAL" depuis PasswordsPage (les clients ne créent jamais de COMPANY)
        scope:    defaultScope,
        // Mot de passe : envoyé uniquement s'il est rempli
        ...(data.encryptedPassword?.trim()
          ? { encryptedPassword: data.encryptedPassword }
          : {}),
      };

      if (isEdit) {
        await updateCredential(editData.id, payload);
      } else {
        await createCredential(payload);
      }

      onSave(); // recharge la liste dans PasswordsPage

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="itm-overlay" onClick={onClose}>
      <div className="itm-modal form-modal" onClick={e => e.stopPropagation()}>
        <button className="itm-close-btn" onClick={onClose}><i className="bi bi-x-lg"/></button>

        {/* Bannière colorée */}
        <div className="form-banner">{SVG[type.id]}</div>

        {/* Corps */}
        <div className="form-body">
          <h5 className="itm-modal-title mb-1">
            {isEdit ? "Modifier" : "Ajouter"} — {type.label}
          </h5>

          {/* Erreur */}
          {error && (
            <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 13, color: "#fa5252" }}>
              <i className="bi bi-exclamation-triangle-fill me-2"/>{error}
            </div>
          )}

          <CredentialForm typeId={type.id} data={data} onChange={handleChange} isEdit={isEdit}/>

          {/* Boutons */}
          <div className="form-actions">
            <button type="button" className="itm-btn-ghost" onClick={onClose}>
              Annuler
            </button>
            <button
              type="button"
              className="itm-btn-primary"
              style={{ "--btn-color": type.bg }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading
                ? <><i className="bi bi-arrow-repeat spin me-1"/>Enregistrement…</>
                : <><i className="bi bi-check-lg me-1"/>{isEdit ? "Mettre à jour" : "Enregistrer"}</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  COMPOSANT EXPORTÉ
//
//  Props :
//   - editData      : credential à modifier (null = création)
//   - defaultScope  : "PERSONAL" | "COMPANY" — transmis au payload lors de la création
//   - onExternalSave  : callback après sauvegarde réussie
//   - onExternalClose : callback pour fermer la modale
// ─────────────────────────────────────────────

export default function CredentialModal({ editData, defaultScope = "PERSONAL", onExternalSave, onExternalClose }) {
  // En mode édition → on résout directement le type depuis la credential
  const [selectedType, setSelectedType] = useState(
    editData
      ? TYPES.find(t => t.id === (editData.type || "OTHER").toUpperCase()) || TYPES[TYPES.length - 1]
      : null
  );

  return (
    <>
      {/* Étape 1 — choisir le type (création uniquement) */}
      {!selectedType && (
        <TypePickerModal
          onSelect={t => setSelectedType(t)}
          onClose={onExternalClose}
        />
      )}

      {/* Étape 2 — remplir le formulaire */}
      {selectedType && (
        <FormModal
          type={selectedType}
          editData={editData}
          defaultScope={defaultScope}
          onClose={() => editData ? onExternalClose?.() : setSelectedType(null)}
          onSave={onExternalSave}
        />
      )}

      <style>{CSS}</style>
    </>
  );
}

// ─────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────
const CSS = `
.itm-overlay{position:fixed;inset:0;z-index:1055;background:rgba(10,12,22,.65);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px;animation:itmFade .15s ease}
@keyframes itmFade{from{opacity:0}to{opacity:1}}
.itm-modal{background:var(--navy-mid,#1C1B1A);border:1px solid var(--border,rgba(255,255,255,.08));border-radius:16px;box-shadow:0 32px 100px rgba(0,0,0,.5);position:relative;width:100%;animation:itmSlide .22s cubic-bezier(.34,1.56,.64,1);max-height:92vh}
@keyframes itmSlide{from{transform:translateY(28px);opacity:0}to{transform:none;opacity:1}}
.picker-modal{max-width:500px;padding:28px 24px 24px;overflow-y:auto}
.form-modal{max-width:490px;display:flex;flex-direction:column;overflow:hidden}
.form-banner{width:100%;aspect-ratio:16/6;flex-shrink:0;overflow:hidden;border-radius:16px 16px 0 0}
.form-banner svg{display:block;width:100%;height:100%}
.form-body{padding:22px 24px 20px;overflow-y:auto}
.itm-close-btn{position:absolute;top:12px;right:14px;z-index:2;background:rgba(0,0,0,.35);border:none;cursor:pointer;color:#fff;font-size:13px;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;transition:all .15s}
.itm-close-btn:hover{background:rgba(0,0,0,.55);color:#fff}
.itm-modal-title{font-size:1.1rem;font-weight:700;color:var(--text-1,#e8eaf0);margin-bottom:4px}
.itm-modal-sub{color:var(--text-3,rgba(255,255,255,.45));font-size:.88rem;margin-bottom:20px}
.type-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}
.type-tile{display:flex;flex-direction:column;align-items:stretch;border:1.5px solid var(--border,rgba(255,255,255,.1));border-radius:10px;background:rgba(255,255,255,.04);cursor:pointer;padding:0;overflow:hidden;transition:all .18s}
.type-tile:hover{border-color:var(--accent,#E8412F);background:var(--accent-dim,rgba(232,65,47,.1));transform:translateY(-2px);box-shadow:0 6px 20px rgba(232,65,47,.2)}
.type-banner{width:100%;aspect-ratio:16/9;overflow:hidden;display:block;line-height:0}
.type-banner svg{display:block;width:100%;height:100%}
.type-label{display:block;padding:7px 5px 8px;font-size:.72rem;font-weight:500;color:var(--text-2,rgba(255,255,255,.75));line-height:1.3;text-align:center}
.itm-label{display:block;font-size:.78rem;font-weight:600;color:var(--text-3,rgba(255,255,255,.55));margin-bottom:5px;letter-spacing:.03em;text-transform:uppercase}
.itm-input{display:block;width:100%;background:rgba(255,255,255,.06);border:1.5px solid var(--border,rgba(255,255,255,.1));border-radius:8px;color:var(--text-1,#e8eaf0);padding:9px 12px;font-size:.88rem;transition:border-color .18s,box-shadow .18s;outline:none}
.itm-input:focus{border-color:var(--accent,#E8412F);box-shadow:0 0 0 3px rgba(232,65,47,.2);background:var(--accent-dim,rgba(232,65,47,.08))}
.itm-input::placeholder{color:var(--text-3,rgba(255,255,255,.25))}
textarea.itm-input{resize:vertical}
.input-group{display:flex}
.input-group .itm-input{border-radius:8px 0 0 8px;flex:1}
.itm-eye-btn{background:rgba(255,255,255,.06);border:1.5px solid var(--border,rgba(255,255,255,.1));border-left:none;border-radius:0 8px 8px 0;color:var(--text-3,rgba(255,255,255,.4));padding:0 12px;cursor:pointer;transition:color .15s;flex-shrink:0}
.itm-eye-btn:hover{color:var(--text-1,#e8eaf0)}
.form-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:18px;padding-top:16px;border-top:1px solid var(--border,rgba(255,255,255,.08))}
.itm-btn-ghost{background:rgba(255,255,255,.06);border:1.5px solid var(--border,rgba(255,255,255,.1));border-radius:8px;color:var(--text-2,rgba(255,255,255,.6));padding:8px 20px;font-size:.88rem;cursor:pointer;transition:all .15s}
.itm-btn-ghost:hover{background:rgba(255,255,255,.1);color:var(--text-1,#fff)}
.itm-btn-primary{background:var(--btn-color,var(--accent,#E8412F));border:none;border-radius:8px;color:#fff;padding:8px 22px;font-size:.88rem;font-weight:600;cursor:pointer;transition:filter .18s;display:inline-flex;align-items:center}
.itm-btn-primary:hover{filter:brightness(1.15)}
.itm-btn-primary:disabled{opacity:.6;cursor:not-allowed}
.mb-3{margin-bottom:14px}
`;